import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ChevronDown, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CONTACT, SERVICES, FLAGSHIP_SERVICES } from "@/data/site";
import { tr, locale } from "@/i18n";
import { trackSuccessfulEnquiry } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [params] = useSearchParams();
  const isAdmission = params.get("type") === "admission";
  const tier = params.get("tier");
  const service = SERVICES.find((s) => s.id === params.get("service"));
  const [serviceName, setServiceName] = useState(service ? service.name : "");
  // Public enquiry forms only ever offer the flagship collection plus membership
  // enquiries; tier-exclusive services stay inside the member portal.
  const publicOptions = [...FLAGSHIP_SERVICES, "Membership"];
  const serviceOptions = publicOptions;
  const additionalOptions = (isAdmission ? FLAGSHIP_SERVICES : publicOptions).filter((o) => o !== serviceName);

  const today = new Date().toISOString().slice(0, 10);
  const [appTier, setAppTier] = useState(tier === "corporate" ? "CORPORATE" : "INDIVIDUAL");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    enquiry_date: today,
    preferred_start_date: "",
    dob: "",
    company: "",
    additional_services: "No",
    message: "",
  });
  const [extraServices, setExtraServices] = useState([]);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addExtra = (name) => {
    if (name && !extraServices.includes(name)) setExtraServices([...extraServices, name]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data: enquiryResult } = await axios.post(`${API}/enquiries`, {
        ...form,
        additional_services: form.additional_services,
        additional_services_list: form.additional_services === "Yes" ? extraServices : [],
        requested_service: serviceName || (isAdmission ? "Membership" : ""),
        language: locale(),
        membership_tier: isAdmission ? appTier : "",
        enquiry_type: serviceName
          ? `Service Enquiry — ${serviceName}`
          : isAdmission
            ? `Request for Admission — ${appTier === "CORPORATE" ? "Corporate" : "Individual"} Membership`
            : "General Enquiry",
      });
      if (enquiryResult?.zoho_success === true) {
        trackSuccessfulEnquiry({ isAdmission, requestedService: serviceName, membershipTier: appTier });
      }
      toast(tr("Your enquiry has been received"), {
        description: tr("A member of our team will respond with discretion and care."),
      });
      setForm({ first_name: "", last_name: "", email: "", phone: "", country: "", enquiry_date: today, preferred_start_date: "", dob: "", company: "", additional_services: "No", message: "" });
      setExtraServices([]);
    } catch {
      toast.error(tr("Something went wrong"), { description: tr("Please try again or contact us directly.") });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline={service ? tr("Enquire — {{service}}", { service: tr(service.name) }) : isAdmission ? tr("Request for Admission") : tr("Contact Us")}
        title={tr("Get in")}
        italic={tr("touch.")}
      >
        <p>
          {service
            ? tr("Tell us what you have in mind for {{service}} — a senior advisor will respond personally.", { service: tr(service.name).toLowerCase() })
            : isAdmission
              ? tr("Membership is limited and granted by application. Tell us about yourself and your requirements — our admissions team will respond personally.")
              : tr("Whether a fleeting whim or a lifelong ambition, our team responds with discretion and care.")}
        </p>
      </PageHero>

      <section className="py-28 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline-lux mb-10">{tr("Contact Info")}</p>
              <ul className="space-y-10">
                {[
                  [Phone, "Phone", CONTACT.phone, `tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`, "contact-phone"],
                  [Mail, "Mail", CONTACT.email, `mailto:${CONTACT.email}`, "contact-email"],
                  [MapPin, "Offices", CONTACT.address, null, "contact-address"],
                ].map(([Icon, label, value, href, tid]) => (
                  <li key={label} className="flex items-start gap-6">
                    <span className="w-12 h-12 border border-white/15 flex items-center justify-center shrink-0">
                      <Icon strokeWidth={1.25} size={17} className="text-gold" />
                    </span>
                    <span>
                      <span className="block text-[0.65rem] uppercase tracking-[0.3em] text-white/40 mb-2">{tr(label)}</span>
                      {href ? (
                        <a href={href} data-testid={tid} className="text-white/80 font-light hover:text-gold transition-colors duration-300">
                          {value}
                        </a>
                      ) : (
                        <span data-testid={tid} className="text-white/80 font-light">{value}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-16 text-white/40 font-light text-sm leading-relaxed max-w-sm border-l border-gold/40 pl-6">{tr("Every enquiry is handled personally by a senior advisor. Expect a response within one working day — usually far sooner.")}</p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="border border-white/10 bg-[#0A0A0B] p-8 lg:p-14"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                  {isAdmission && (
                    <div className="relative sm:col-span-2">
                      <label htmlFor="app-tier" className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Application Tier Requested")}</label>
                      <select
                        id="app-tier"
                        data-testid="admission-tier-select"
                        value={appTier}
                        onChange={(e) => setAppTier(e.target.value)}
                        className="input-lux appearance-none pr-8 cursor-pointer"
                      >
                        <option value="INDIVIDUAL" className="bg-[#0A0A0B]">{tr("INDIVIDUAL")}</option>
                        <option value="CORPORATE" className="bg-[#0A0A0B]">{tr("CORPORATE")}</option>
                      </select>
                      <ChevronDown strokeWidth={1.5} size={15} className="absolute right-0 top-9 pointer-events-none text-gold/70" />
                    </div>
                  )}
                  <input
                    data-testid="contact-first-name-input"
                    required
                    value={form.first_name}
                    onChange={set("first_name")}
                    placeholder={tr("First Name")}
                    className="input-lux"
                  />
                  <input
                    data-testid="contact-last-name-input"
                    required
                    value={form.last_name}
                    onChange={set("last_name")}
                    placeholder={tr("Last Name")}
                    className="input-lux"
                  />
                  <input
                    data-testid="contact-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder={tr("Email")}
                    className="input-lux"
                  />
                  <input
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder={tr("Phone (optional)")}
                    className="input-lux"
                  />
                  {isAdmission && (
                    <div>
                      <label htmlFor="dob" className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Date of Birth")}</label>
                      <input
                        id="dob"
                        data-testid="admission-dob-input"
                        type="date"
                        required
                        value={form.dob}
                        onChange={set("dob")}
                        className="input-lux cursor-pointer [color-scheme:dark]"
                      />
                    </div>
                  )}
                  <input
                    data-testid="contact-country-input"
                    value={form.country}
                    onChange={set("country")}
                    placeholder={tr("Country of Residence")}
                    className="input-lux"
                  />
                  {isAdmission && (
                    <input
                      data-testid="admission-company-input"
                      value={form.company}
                      onChange={set("company")}
                      placeholder={tr("Company Name (if applicable)")}
                      className="input-lux"
                    />
                  )}
                  <div>
                    <label htmlFor="enquiry-date" className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">
                      {isAdmission ? tr("Date of Application") : tr("Date of Enquiry")}
                    </label>
                    <input
                      id="enquiry-date"
                      data-testid="contact-date-input"
                      type="date"
                      value={form.enquiry_date}
                      onChange={set("enquiry_date")}
                      className="input-lux cursor-pointer [color-scheme:dark]"
                    />
                  </div>
                  {isAdmission && (
                    <div>
                      <label htmlFor="preferred-start" className="block text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mb-2">{tr("Preferred Membership Start Date")}</label>
                      <input
                        id="preferred-start"
                        data-testid="admission-start-date-input"
                        type="date"
                        value={form.preferred_start_date}
                        onChange={set("preferred_start_date")}
                        className="input-lux cursor-pointer [color-scheme:dark]"
                      />
                    </div>
                  )}
                  {!isAdmission && (
                    <div className="relative sm:col-span-2">
                      <select
                        data-testid="contact-service-select"
                        value={serviceName}
                        onChange={(e) => setServiceName(e.target.value)}
                        className="input-lux appearance-none pr-8 cursor-pointer"
                      >
                        <option value="" className="bg-[#0A0A0B]">{tr("Which service do you require? (optional)")}</option>
                        {serviceOptions.map((o) => (
                          <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>
                        ))}
                      </select>
                      <ChevronDown strokeWidth={1.5} size={15} className="absolute right-0 top-4 pointer-events-none text-gold/70" />
                    </div>
                  )}
                  <div className="relative">
                    <select
                      data-testid="contact-additional-services-select"
                      value={form.additional_services}
                      onChange={set("additional_services")}
                      className="input-lux appearance-none pr-8 cursor-pointer"
                    >
                      <option value="No" className="bg-[#0A0A0B]">{tr("Any additional services? — No")}</option>
                      <option value="Yes" className="bg-[#0A0A0B]">{tr("Any additional services? — Yes")}</option>
                    </select>
                    <ChevronDown strokeWidth={1.5} size={15} className="absolute right-0 top-4 pointer-events-none text-gold/70" />
                  </div>
                  {form.additional_services === "Yes" && (
                    <div className="sm:col-span-2">
                      <div className="relative">
                        <select
                          data-testid="contact-extra-services-select"
                          value=""
                          onChange={(e) => addExtra(e.target.value)}
                          className="input-lux appearance-none pr-8 cursor-pointer"
                        >
                          <option value="" className="bg-[#0A0A0B]">{tr("Select the additional services you require…")}</option>
                          {additionalOptions.map((o) => (
                            <option key={o} value={o} className="bg-[#0A0A0B]">{tr(o)}</option>
                          ))}
                        </select>
                        <ChevronDown strokeWidth={1.5} size={15} className="absolute right-0 top-4 pointer-events-none text-gold/70" />
                      </div>
                      {extraServices.length > 0 && (
                        <div className="flex flex-wrap gap-2.5 mt-5" data-testid="contact-extra-services-chips">
                          {extraServices.map((s) => (
                            <span key={s} className="flex items-center gap-2.5 border border-gold/35 text-gold/90 text-[0.6rem] uppercase tracking-[0.2em] px-3.5 py-2">
                              {tr(s)}
                              <button
                                type="button"
                                data-testid={`contact-extra-remove-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                onClick={() => setExtraServices(extraServices.filter((x) => x !== s))}
                                aria-label={tr("Remove {{service}}", { service: tr(s) })}
                                className="text-gold/60 hover:text-gold transition-colors"
                              >
                                <X strokeWidth={1.5} size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={isAdmission ? 8 : 5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={service ? tr("Tell us about your {{service}} requirements…", { service: tr(service.name).toLowerCase() }) : isAdmission ? tr("Reason for admission — tell us about yourself and why you seek membership…") : tr("How can we assist you?")}
                  className="input-lux mt-8 resize-none"
                />
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-12 w-full border border-gold/70 text-gold text-xs uppercase tracking-[0.35em] py-5 hover:bg-gold hover:text-black transition-colors duration-500 disabled:opacity-50"
                >
                  {sending ? tr("Sending…") : service ? tr("Send Enquiry") : isAdmission ? tr("Submit Request for Admission") : tr("Send Message")}
                </button>
                <p className="mt-6 text-[0.65rem] text-white/30 tracking-wider leading-relaxed">{tr("By submitting this form you confirm that you have read and agree with the Terms & Conditions and Privacy Policy of Delux Concierge LTD.")}</p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
